import { MAX_EVENTS_PER_REQUEST, MAX_EVENT_AGE_MS, type ClientEvent } from '@lyrmo/shared';

// What the writer did, told to the API for Lyrmo's own insights: that it happened, and how,
// never the text. The API keeps no more than its schema allows.

/** Sends events to the API; `keepalive` lets the request finish after the page closes. */
export type SendEvents = (events: ClientEvent[], keepalive: boolean) => Promise<void>;

export interface Tracker {
  /** Notes that something happened, to send with the next batch. */
  readonly track: (event: ClientEvent) => void;
  /** Sends what's waiting now, as when the page is hidden or about to close. */
  readonly flush: (keepalive?: boolean) => void;
}

/** How long events wait, so a few go together. */
export const BATCH_DELAY_MS = 5_000;

interface TrackerOptions {
  readonly now?: () => number;
}

/** Events noted, sent together a few seconds after the first of them, or when flushed. */
export function createTracker(send: SendEvents, { now = Date.now }: TrackerOptions = {}): Tracker {
  const waiting: { readonly event: ClientEvent; readonly at: number }[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;

  function flush(keepalive = false) {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    const time = now();
    while (waiting.length > 0) {
      const batch = waiting.splice(0, MAX_EVENTS_PER_REQUEST).map(({ event, at }) => ({
        ...event,
        ago: Math.min(MAX_EVENT_AGE_MS, Math.max(0, time - at)),
      }));
      // An event that can't be sent is gone: it's only ever counted, never needed.
      try {
        send(batch, keepalive).catch(() => undefined);
      } catch {
        // Nothing to send it with.
      }
    }
  }

  return {
    track(event) {
      waiting.push({ event, at: now() });
      timer ??= setTimeout(() => {
        flush();
      }, BATCH_DELAY_MS);
    },
    flush,
  };
}

/** How long the site can go unused before coming back to it is a new visit. */
export const VISIT_GAP_MS = 30 * 60_000;
const SEEN_KEY = 'lyrmo:last-seen';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The browser's local storage, if the page may use it. */
export function localStore(): Store | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Notes the site as used at `time`, saying whether that begins a visit: nothing was done on it,
 * in any tab, for VISIT_GAP_MS.
 */
export function noteUse(store: Pick<Store, 'getItem' | 'setItem'> | null, time: number): boolean {
  let last = Number.NaN;
  try {
    last = Number(store?.getItem(SEEN_KEY) ?? Number.NaN);
    store?.setItem(SEEN_KEY, String(time));
  } catch {
    // Storage can be full or refused; then each page load is a visit.
  }
  return !(time - last < VISIT_GAP_MS);
}

/** A tag of the address, if it has one that isn't empty. */
function tag(params: URLSearchParams, name: string): string | undefined {
  const value = params.get(name)?.trim().slice(0, 64);
  // An empty tag is no tag.
  if (!value) return undefined;
  return value;
}

/**
 * Where the visit came from: the hostname of another site that linked here, never the page or
 * what was searched, and the address's campaign tags.
 */
export function arrivalOf(
  location: Pick<Location, 'search' | 'hostname'>,
  referrer: string,
): Pick<Extract<ClientEvent, { event: 'visit' }>, 'referrer' | 'campaign'> {
  let host: string | undefined;
  try {
    host = referrer ? new URL(referrer).hostname : undefined;
  } catch {
    host = undefined;
  }
  const params = new URLSearchParams(location.search);
  const campaign = {
    source: tag(params, 'utm_source'),
    medium: tag(params, 'utm_medium'),
    name: tag(params, 'utm_campaign'),
  };
  const tagged = Object.values(campaign).some((value) => value !== undefined);
  return {
    ...(host && host !== location.hostname && { referrer: host }),
    ...(tagged && { campaign }),
  };
}

/** Forgets when the site was last used, as noteUse keeps it. */
export function forgetUse(store: Pick<Store, 'removeItem'> | null): void {
  try {
    store?.removeItem(SEEN_KEY);
  } catch {
    // Storage refused: there's nothing to forget.
  }
}
